import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { skillsApi } from '../../utils/api';
import WizardLayout from '../../components/builder/WizardLayout';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import GlassCard from '../../components/GlassCard';
import { Sparkles, Loader, RefreshCw, Check, Edit, Package, CheckCircle, Circle } from 'lucide-react';
import { toast } from 'sonner';

const Step2Generate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingPackage, setGeneratingPackage] = useState(false);
  const [packageProgress, setPackageProgress] = useState(0);
  const [skillInfo, setSkillInfo] = useState(null);
  const [generated, setGenerated] = useState(false);
  const [editMode, setEditMode] = useState({
    inputs: false,
    instructions: false,
    outputFormat: false
  });
  
  const [formData, setFormData] = useState({
    inputs: [],
    instructions: '',
    outputFormat: 'markdown',
    outputStructure: '',
    keyCapabilities: [],
    howItWorks: '',
    specialFeatures: []
  });

  useEffect(() => {
    loadSkillAndGenerate();
  }, [id]);

  const loadSkillAndGenerate = async () => {
    try {
      const response = await skillsApi.get(id);
      const skill = response.data;
      setSkillInfo(skill);
      
      // Check if already generated
      if (skill.builder_data?.inputs && skill.builder_data?.instructions) {
        setFormData({
          inputs: skill.builder_data.inputs || [],
          instructions: skill.builder_data.instructions || '',
          outputFormat: skill.builder_data.outputFormat || 'markdown',
          outputStructure: skill.builder_data.outputStructure || '',
          keyCapabilities: skill.builder_data.keyCapabilities || [],
          howItWorks: skill.builder_data.howItWorks || '',
          specialFeatures: skill.builder_data.specialFeatures || []
        });
        setGenerated(true);
      } else {
        // Auto-generate if not already done
        await generateWithClaude(skill);
      }
    } catch (error) {
      console.error('Failed to load skill', error);
      toast.error('Failed to load skill');
    }
  };

  const generateWithClaude = async (skill = skillInfo) => {
    setGenerating(true);
    try {
      const { data } = await skillsApi.generateSpec({
        skillName: skill.name,
        skillDescription: skill.description,
        category: skill.category_id
      });

      const spec = data.specification;
      
      if (!spec) {
        throw new Error('No specification returned from AI');
      }
      
      setFormData({
        inputs: spec.inputs || [],
        instructions: spec.instructions || '',
        outputFormat: spec.outputFormat || 'markdown',
        outputStructure: spec.outputStructure || '',
        keyCapabilities: spec.keyCapabilities || [],
        howItWorks: spec.howItWorks || '',
        specialFeatures: spec.specialFeatures || []
      });
      setGenerated(true);
      toast.success('✨ Skill specification generated!');
    } catch (error) {
      console.error('Failed to generate', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to generate specification');
    } finally {
      setGenerating(false);
    }
  };

  const updateInput = (index, field, value) => {
    const newInputs = [...formData.inputs];
    newInputs[index] = { ...newInputs[index], [field]: value };
    setFormData({ ...formData, inputs: newInputs });
  };

  const handleNext = async () => {
    if (!generated) {
      toast.error('Generate the skill specification first');
      return;
    }

    setLoading(true);
    
    try {
      // Just save the specification - generation happens in Step 3
      await skillsApi.update(id, {
        builder_step: 2,
        builder_data: formData
      });

      toast.success('Specification saved!');
      
      // Navigate to Step 3 where generation will begin
      navigate(`/builder/${id}/step-3`);
      
    } catch (error) {
      console.error('Failed to save', error);
      const errorMessage = error.message || 'Failed to save specification';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/builder/${id}/step-1`);
  };

  if (!skillInfo) {
    return <div>Loading...</div>;
  }

  return (
    <WizardLayout
      currentStep={2}
      onBack={handleBack}
      onNext={handleNext}
      nextLabel={loading ? "Saving..." : "Next: Publish Skill"}
      nextDisabled={loading || !generated}
    >
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-manrope font-bold text-text-dark dark:text-dark-text mb-2">
            Generate Skill Specification
          </h2>
          <p className="text-text-gray dark:text-dark-text-secondary">
            Let Claude create the complete skill specification based on your description
          </p>
        </div>

        {generating ? (
          <GlassCard className="p-12 text-center">
            <div className="max-w-md mx-auto space-y-6">
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-primary to-primary-dark rounded-2xl flex items-center justify-center animate-pulse">
                <Sparkles className="h-10 w-10 text-white" />
              </div>
              
              <div>
                <h3 className="text-2xl font-semibold text-text-dark dark:text-dark-text mb-3">
                  Generating your skill specification...
                </h3>
                <p className="text-text-gray dark:text-dark-text-secondary mb-6">
                  Claude is analyzing "{skillInfo?.name}" and creating:
                </p>
                <ul className="text-left mt-4 space-y-3 text-sm">
                  <li className="flex items-center gap-3 text-text-gray dark:text-dark-text-secondary">
                    <Loader className="h-4 w-4 text-primary animate-spin" />
                    Smart input fields with defaults
                  </li>
                  <li className="flex items-center gap-3 text-text-gray dark:text-dark-text-secondary">
                    <Loader className="h-4 w-4 text-primary animate-spin" />
                    Comprehensive behavior instructions
                  </li>
                  <li className="flex items-center gap-3 text-text-gray dark:text-dark-text-secondary">
                    <Loader className="h-4 w-4 text-primary animate-spin" />
                    Output format and structure
                  </li>
                  <li className="flex items-center gap-3 text-text-gray dark:text-dark-text-secondary">
                    <Loader className="h-4 w-4 text-primary animate-spin" />
                    Key capabilities and features
                  </li>
                </ul>
              </div>
              
              <div className="text-sm text-text-light dark:text-dark-text-secondary">
                This usually takes 5-10 seconds...
              </div>
            </div>
          </GlassCard>
        ) : generated ? (
          <div className="space-y-6">
            {/* Regenerate button */}
            <div className="flex justify-end">
              <Button
                type="button"
                onClick={generateWithClaude}
                disabled={generating}
                variant="outline"
                className="rounded-xl"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Regenerate
              </Button>
            </div>

            {/* Key Capabilities */}
            {formData.keyCapabilities.length > 0 && (
              <GlassCard className="p-6">
                <h3 className="text-lg font-semibold text-text-dark dark:text-dark-text mb-3">
                  Key Capabilities
                </h3>
                <ul className="space-y-2">
                  {formData.keyCapabilities.map((cap, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-text-gray dark:text-dark-text-secondary">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{cap}</span>
                    </li>
                  ))}
                </ul>
              </GlassCard>
            )}

            {/* How It Works */}
            {formData.howItWorks && (
              <GlassCard className="p-6">
                <h3 className="text-lg font-semibold text-text-dark dark:text-dark-text mb-3">
                  How It Works
                </h3>
                <p className="text-text-gray dark:text-dark-text-secondary">
                  {formData.howItWorks}
                </p>
              </GlassCard>
            )}

            {/* Inputs */}
            <GlassCard className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-text-dark dark:text-dark-text">
                  Inputs ({formData.inputs.length})
                </h3>
                <button
                  onClick={() => setEditMode({ ...editMode, inputs: !editMode.inputs })}
                  className="text-primary hover:text-primary-dark flex items-center gap-1 text-sm"
                >
                  <Edit className="h-4 w-4" />
                  {editMode.inputs ? 'Done' : 'Edit'}
                </button>
              </div>
              
              <div className="space-y-4">
                {formData.inputs.map((input, idx) => (
                  <div key={idx} className="p-4 bg-gray-50 dark:bg-dark-bg rounded-xl space-y-2">
                    {editMode.inputs ? (
                      <>
                        <Input
                          value={input.label}
                          onChange={(e) => updateInput(idx, 'label', e.target.value)}
                          placeholder="Input label"
                        />
                        <Input
                          value={input.description}
                          onChange={(e) => updateInput(idx, 'description', e.target.value)}
                          placeholder="Description"
                        />
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-text-dark dark:text-dark-text">
                            {input.label}
                          </span>
                          <span className="text-xs px-2 py-0.5 bg-primary/20 text-primary rounded">
                            {input.type}
                          </span>
                          {input.required && (
                            <span className="text-xs px-2 py-0.5 bg-red-100 dark:bg-red-900/20 text-red-600 rounded">
                              Required
                            </span>
                          )}
                        </div>
                        {input.description && (
                          <p className="text-sm text-text-gray dark:text-dark-text-secondary">
                            {input.description}
                          </p>
                        )}
                        {input.example && (
                          <p className="text-xs text-text-light dark:text-dark-text-secondary">
                            Example: <code className="bg-gray-200 dark:bg-dark-border px-1 rounded">{input.example}</code>
                          </p>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </GlassCard>

            {/* Instructions */}
            <GlassCard className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-text-dark dark:text-dark-text">
                  Behavior Instructions
                </h3>
                <button
                  onClick={() => setEditMode({ ...editMode, instructions: !editMode.instructions })}
                  className="text-primary hover:text-primary-dark flex items-center gap-1 text-sm"
                >
                  <Edit className="h-4 w-4" />
                  {editMode.instructions ? 'Done' : 'Edit'}
                </button>
              </div>
              
              {editMode.instructions ? (
                <Textarea
                  value={formData.instructions}
                  onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                  rows={10}
                  className="font-mono text-sm"
                />
              ) : (
                <div className="bg-gray-50 dark:bg-dark-bg rounded-xl p-4">
                  <pre className="whitespace-pre-wrap text-sm text-text-dark dark:text-dark-text font-sans">
                    {formData.instructions}
                  </pre>
                </div>
              )}
            </GlassCard>

            {/* Output Format */}
            <GlassCard className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-text-dark dark:text-dark-text">
                  Output Format
                </h3>
                <button
                  onClick={() => setEditMode({ ...editMode, outputFormat: !editMode.outputFormat })}
                  className="text-primary hover:text-primary-dark flex items-center gap-1 text-sm"
                >
                  <Edit className="h-4 w-4" />
                  {editMode.outputFormat ? 'Done' : 'Edit'}
                </button>
              </div>
              
              {editMode.outputFormat ? (
                <div className="space-y-3">
                  <select
                    value={formData.outputFormat}
                    onChange={(e) => setFormData({ ...formData, outputFormat: e.target.value })}
                    className="w-full px-4 py-2 rounded-xl border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-secondary"
                  >
                    <option value="markdown">Markdown</option>
                    <option value="plaintext">Plain Text</option>
                    <option value="json">JSON</option>
                    <option value="html">HTML</option>
                  </select>
                  <Textarea
                    value={formData.outputStructure}
                    onChange={(e) => setFormData({ ...formData, outputStructure: e.target.value })}
                    placeholder="Output structure/template..."
                    rows={6}
                    className="font-mono text-sm"
                  />
                </div>
              ) : (
                <div>
                  <div className="mb-2">
                    <span className="text-sm px-3 py-1 bg-primary/20 text-primary rounded-lg font-medium">
                      {formData.outputFormat}
                    </span>
                  </div>
                  {formData.outputStructure && (
                    <div className="bg-gray-50 dark:bg-dark-bg rounded-xl p-4">
                      <pre className="whitespace-pre-wrap text-sm text-text-dark dark:text-dark-text font-mono">
                        {formData.outputStructure}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </GlassCard>

            {/* Special Features */}
            {formData.specialFeatures.length > 0 && (
              <GlassCard className="p-6">
                <h3 className="text-lg font-semibold text-text-dark dark:text-dark-text mb-3">
                  Special Features
                </h3>
                <ul className="space-y-2">
                  {formData.specialFeatures.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-text-gray dark:text-dark-text-secondary">
                      <Sparkles className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </GlassCard>
            )}
          </div>
        ) : null}
      </div>
    </WizardLayout>
  );
};

export default Step2Generate;
